import { z } from "zod";
import { AgentHelperState, AgentRole } from "./agent-roles.js";

/*
 * Autopilot (docs/autopilot.md): a monitoring agent on the customer's server
 * reporting to our hub. The panel only ever talks to our API; these are our
 * own shapes, not the hub's. Read and warn only: nothing here changes a server.
 */

/**
 * Where an agent stands:
 * - `waiting` — enrolled, the agent has not connected yet (token valid);
 * - `connected` — the agent connected, no report yet;
 * - `reporting` — reports arrive;
 * - `stale` — no report for a while (server off, agent stopped, network);
 * - `expired` — the enrolment token ran out before the agent connected.
 */
export const AutopilotStatus = z.enum(["waiting", "connected", "reporting", "stale", "expired"]);
export type AutopilotStatus = z.infer<typeof AutopilotStatus>;

/** The overall health shown as a badge: worst of the warnings, `unknown` without reports. */
export const AutopilotHealth = z.enum(["ok", "warn", "critical", "unknown"]);
export type AutopilotHealth = z.infer<typeof AutopilotHealth>;

/** The latest numbers of a server, kept by our API from the hub every minute. */
export const AutopilotSummary = z.object({
  cpuPercent: z.number().nullable(),
  memoryPercent: z.number().nullable(),
  /** Usage of the root filesystem. */
  diskPercent: z.number().nullable(),
  /** Load average over 1, 5 and 15 minutes. */
  load: z.tuple([z.number(), z.number(), z.number()]).nullable(),
  uptimeSeconds: z.number().nullable(),
  /** systemd units the agent watches, and how many of them failed. */
  services: z.object({ total: z.number().int(), failed: z.number().int() }).nullable(),
  /**
   * Docker containers and how many of them need a look (the same rules as
   * the tab's warnings); null when container monitoring is off or nothing
   * was read yet.
   */
  containers: z
    .object({
      total: z.number().int(),
      running: z.number().int(),
      unhealthy: z.number().int(),
      restarting: z.number().int(),
      stopped: z.number().int(),
    })
    .nullable(),
  /** Pending package updates, security ones among them; null before the agent checked. */
  updates: z.object({ total: z.number().int(), security: z.number().int() }).nullable(),
  /**
   * TLS certificates the agent watches: expiring within the warning window,
   * expired, and the nearest expiry; null while the hub does not collect them.
   */
  certificates: z
    .object({
      total: z.number().int(),
      expiring: z.number().int(),
      expired: z.number().int(),
      nextExpiresAt: z.iso.datetime().nullable(),
    })
    .nullable(),
});
export type AutopilotSummary = z.infer<typeof AutopilotSummary>;

/** Autopilot of one server as lists and cards show it. */
export const AutopilotAgent = z.object({
  serverId: z.uuid(),
  status: AutopilotStatus,
  health: AutopilotHealth,
  /** Container monitoring: the agent is in the server's `docker` group (root-equivalent). */
  containers: z.boolean(),
  /** Agent version as the agent reports it; null before the first report. */
  version: z.string().nullable(),
  /** The version new installs get (our manifest's `current`); null without a usable build. */
  latestVersion: z.string().nullable(),
  /**
   * The agent runs an older version than `latestVersion`. The agent never
   * updates itself (owner's decisions 3, 4): running the install command
   * again updates it.
   */
  updateAvailable: z.boolean(),
  createdAt: z.iso.datetime(),
  /** First connection of the agent. */
  enrolledAt: z.iso.datetime().nullable(),
  /** Last report the hub received. */
  lastSeenAt: z.iso.datetime().nullable(),
  /** While `waiting`: when the enrolment token stops working. */
  tokenExpiresAt: z.iso.datetime().nullable(),
  summary: AutopilotSummary.nullable(),
  /** What may be done on the server besides monitoring, as chosen in the panel. */
  role: AgentRole,
  /** The helper's own answer the last time it was asked; null before that or without a role. */
  helper: AgentHelperState.nullable(),
});
export type AutopilotAgent = z.infer<typeof AutopilotAgent>;

/** `GET /projects/:projectId/autopilot` — whether Autopilot can be used here, and every agent. */
export const AutopilotProject = z.object({
  /** False when this installation has no hub configured: the panel hides Autopilot. */
  available: z.boolean(),
  agents: z.array(AutopilotAgent),
});
export type AutopilotProject = z.infer<typeof AutopilotProject>;

/** Turning Autopilot on at server creation (`CreateServerRequest.autopilot`) or later. */
export const AutopilotOptions = z.object({
  /** Watch Docker containers too; on by default (owner's decision 8). */
  containers: z.boolean().default(true),
  /** The agent role (docs/autopilot.md "Agent roles"); monitoring only by default. */
  role: AgentRole.default("monitoring"),
});
export type AutopilotOptions = z.infer<typeof AutopilotOptions>;

/**
 * `POST /projects/:projectId/servers/:serverId/autopilot` — an install command
 * with a fresh one-time token, shown once. Running it on the server installs
 * the agent; the token stops working after `expiresAt` or the first connection.
 */
export const EnableAutopilotResponse = z.object({
  agent: AutopilotAgent,
  /** One line to run as root on the server (or to type into the console). */
  command: z.string(),
  expiresAt: z.iso.datetime(),
});
export type EnableAutopilotResponse = z.infer<typeof EnableAutopilotResponse>;

/** `DELETE …/autopilot` — the hub forgets the server; this command removes the agent from it. */
export const DisableAutopilotResponse = z.object({ uninstallCommand: z.string() });
export type DisableAutopilotResponse = z.infer<typeof DisableAutopilotResponse>;

export const ContainerState = z.enum([
  "running",
  "restarting",
  "paused",
  "created",
  "exited",
  "dead",
  "unknown",
]);
export type ContainerState = z.infer<typeof ContainerState>;

export const ContainerHealth = z.enum(["healthy", "unhealthy", "starting", "none"]);
export type ContainerHealth = z.infer<typeof ContainerHealth>;

export const AutopilotContainer = z.object({
  id: z.string(),
  name: z.string(),
  image: z.string().nullable(),
  state: ContainerState,
  /** The engine's own words, e.g. `Up 3 hours`. */
  status: z.string().nullable(),
  health: ContainerHealth,
  cpuPercent: z.number().nullable(),
  memoryBytes: z.number().nullable(),
  /** Restarts since the container was created; null when the agent does not report it. */
  restarts: z.number().int().nullable(),
});
export type AutopilotContainer = z.infer<typeof AutopilotContainer>;

export const ServiceState = z.enum([
  "active",
  "inactive",
  "failed",
  "activating",
  "deactivating",
  "reloading",
  "unknown",
]);
export type ServiceState = z.infer<typeof ServiceState>;

export const AutopilotService = z.object({
  name: z.string(),
  state: ServiceState,
  /** systemd's sub-state, e.g. `running`, `exited`, `dead`. */
  sub: z.string().nullable(),
  cpuPercent: z.number().nullable(),
  memoryBytes: z.number().nullable(),
  restarts: z.number().int().nullable(),
});
export type AutopilotService = z.infer<typeof AutopilotService>;

export const PackageUpdate = z.object({
  name: z.string(),
  current: z.string().nullable(),
  available: z.string().nullable(),
  security: z.boolean(),
});
export type PackageUpdate = z.infer<typeof PackageUpdate>;

/** Pending package updates. The panel shows how to apply them and never runs it (decision 4). */
export const PackageUpdates = z.object({
  /** apt, dnf, zypper, pacman, apk; null when unknown. */
  manager: z.string().nullable(),
  total: z.number().int(),
  security: z.number().int(),
  packages: z.array(PackageUpdate),
  /** The command that applies them, to run by hand as root. */
  command: z.string().nullable(),
  checkedAt: z.iso.datetime().nullable(),
});
export type PackageUpdates = z.infer<typeof PackageUpdates>;

export const AutopilotCertificate = z.object({
  host: z.string(),
  expiresAt: z.iso.datetime(),
});
export type AutopilotCertificate = z.infer<typeof AutopilotCertificate>;

/** An SSH login (decision 9: visible to everyone with access to the project, kept 90 days). */
export const SshLogin = z.object({
  at: z.iso.datetime(),
  user: z.string(),
  ip: z.string().nullable(),
  method: z.string().nullable(),
  success: z.boolean(),
});
export type SshLogin = z.infer<typeof SshLogin>;

export const AutopilotWarningKind = z.enum([
  "not_reporting",
  "memory_high",
  "disk_full",
  "service_failed",
  "container_unhealthy",
  "container_stopped",
  "container_restarting",
  "updates_security",
  "updates_pending",
  "certificate_expiring",
  "certificate_expired",
]);
export type AutopilotWarningKind = z.infer<typeof AutopilotWarningKind>;

/** Something worth a look, worked out by our API from the reports. */
export const AutopilotWarning = z.object({
  kind: AutopilotWarningKind,
  severity: z.enum(["warn", "critical"]),
  /** What it is about: a service, container, filesystem, host name, or the package count. */
  subject: z.string(),
  /** A command that fixes it, to run by hand; never run by us. */
  command: z.string().nullable(),
});
export type AutopilotWarning = z.infer<typeof AutopilotWarning>;

export const AutopilotSystem = z.object({
  hostname: z.string().nullable(),
  os: z.string().nullable(),
  kernel: z.string().nullable(),
  cpuModel: z.string().nullable(),
  /** As the agent reports it, e.g. `amd64`, `arm64`. */
  arch: z.string().nullable(),
  cores: z.number().int().nullable(),
  memoryBytes: z.number().nullable(),
  uptimeSeconds: z.number().nullable(),
});
export type AutopilotSystem = z.infer<typeof AutopilotSystem>;

/** `GET /projects/:projectId/servers/:serverId/autopilot` — everything the tab shows but charts. */
/** A report section of the Autopilot tab. */
export const AutopilotReportSection = z.enum([
  "containers",
  "services",
  "updates",
  "certificates",
  "sshLogins",
]);
export type AutopilotReportSection = z.infer<typeof AutopilotReportSection>;

export const AutopilotDetails = z.object({
  available: z.boolean(),
  /**
   * Sections the hub could not read because this server's agent is offline
   * (the server is off, the agent stopped, a live call to it failed). The
   * hub itself answers: the tab shows them as «Агент не на связи», not as
   * an error (reason `autopilot_agent_offline`).
   */
  offline: z.array(AutopilotReportSection),
  /** Null when Autopilot is off for this server. */
  agent: AutopilotAgent.nullable(),
  system: AutopilotSystem.nullable(),
  /** Null when container monitoring is off. */
  containers: z.array(AutopilotContainer).nullable(),
  services: z.array(AutopilotService),
  updates: PackageUpdates.nullable(),
  certificates: z.array(AutopilotCertificate),
  /** Null while the agent does not collect them (upstream builds; the security radar adds them). */
  sshLogins: z.array(SshLogin).nullable(),
  warnings: z.array(AutopilotWarning),
});
export type AutopilotDetails = z.infer<typeof AutopilotDetails>;

export const AUTOPILOT_RANGES = ["1h", "24h", "7d", "30d"] as const;
export const AutopilotRange = z.enum(AUTOPILOT_RANGES);
export type AutopilotRange = z.infer<typeof AutopilotRange>;

/** `[unix seconds, value]`; null is a gap. */
const Points = z.array(z.tuple([z.number(), z.number().nullable()]));

export const AutopilotSeries = z.object({
  /** `cpu`, `memory`, `swap`, `load1`…, a filesystem (`/`, `/data`), `read`, `in`, a sensor name. */
  name: z.string(),
  points: Points,
});
export type AutopilotSeries = z.infer<typeof AutopilotSeries>;

/**
 * `GET …/autopilot/metrics?range=` — one set of series per chart, units fixed
 * per chart: percent (cpu, memory, disk), bytes (swap), load, bytes per
 * second (disk I/O, network), degrees Celsius (temperatures).
 */
export const AutopilotMetrics = z.object({
  range: AutopilotRange,
  stepSeconds: z.number().int().positive(),
  cpu: z.array(AutopilotSeries),
  memory: z.array(AutopilotSeries),
  swap: z.array(AutopilotSeries),
  load: z.array(AutopilotSeries),
  diskUsage: z.array(AutopilotSeries),
  diskIo: z.array(AutopilotSeries),
  network: z.array(AutopilotSeries),
  temperatures: z.array(AutopilotSeries),
});
export type AutopilotMetrics = z.infer<typeof AutopilotMetrics>;

/** The agent build the download endpoint serves (`/autopilot/agent/manifest.json`). */
export const AgentArch = z.enum(["amd64", "arm64"]);
export type AgentArch = z.infer<typeof AgentArch>;

export const AgentManifest = z.object({
  /** The version new installs get. */
  current: z.string(),
  /** Version → SHA-256 (hex) of the binary per architecture. */
  versions: z.record(z.string(), z.partialRecord(AgentArch, z.string().regex(/^[0-9a-f]{64}$/))),
});
export type AgentManifest = z.infer<typeof AgentManifest>;
